import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, Download, Loader2, Check, X as XIcon, Clock, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

/**
 * GreenApiaProPanel — Pro/Ads side: request a Green AP-IA certificate.
 * Once approved, the pro can download the PDF certificate + Green IA label SVG.
 * Fully translated for the 13 supported languages via inline dictionary
 * (kept lean, only strings shown in the panel).
 */
const DICT = {
  fr: {
    title: 'Green AP-IA — Certificat éthique anti-deepfake',
    intro: "Rejoignez l'initiative Green IA de Destine. Une fois votre demande approuvée par un administrateur, vous recevrez un certificat unique + un label \"Green IA\" à afficher dans votre outil ou application. Votre clé AP-IA pourra alors interroger notre registre Green IA avant toute génération.",
    company: 'Nom de l\u2019entreprise / association *',
    country: 'Pays *',
    tool: 'Nom de l\u2019outil ou app IA *',
    url: 'URL publique de l\u2019outil (optionnel)',
    email: 'Email de contact *',
    justification: 'Justification (activité IA, engagement anti-deepfake…)',
    submit: 'Soumettre la demande',
    submitting: 'Envoi…',
    ok: 'Demande envoyée — un admin la vérifiera.',
    ko: 'Erreur',
    duplicate: 'Une demande est déjà en cours.',
    myRequests: 'Mes demandes',
    statusPending: 'En attente de validation',
    statusApproved: 'Approuvé',
    statusRejected: 'Refusé',
    certNumber: 'N° de certificat',
    downloadCert: 'Télécharger le certificat PDF',
    downloadLabel: 'Télécharger le label SVG',
    copyEndpoint: 'Copier l\u2019URL de l\u2019API check',
    copied: 'Copié',
    hint: 'Après approbation, votre clé AP-IA (scope "Green AP-IA") pourra appeler /api/green-ia/external-check',
    proOnly: 'Réservé aux comptes PRO vérifiés',
    why: 'Pourquoi Green IA ? Les deepfakes explosent : visages et voix volés servent aux arnaques, au harcèlement et à la désinformation. Green IA a été créé par Destine pour redonner le contrôle aux personnes : chacun peut enregistrer son visage et sa voix (stockés uniquement en hash SHA-256 irréversibles). Tout outil IA certifié Green IA interroge ce registre AVANT chaque génération et bloque automatiquement tout deepfake d\'une personne protégée.',
    sdkTitle: 'SDK Green AP-IA — intégrez la vérification en 3 lignes',
    sdkNote: 'Appelez ce endpoint avant chaque génération d\'image, vidéo ou voix. Si `protected` est true, votre outil doit refuser la génération pour rester certifié.',
  },
  en: {
    title: 'Green AP-IA — Ethical anti-deepfake certificate',
    intro: 'Join the Destine Green IA initiative. Once your request is approved by an admin, you will receive a unique certificate + a "Green IA" label to display on your tool or app. Your AP-IA key will then be able to query our Green IA registry before any generation.',
    company: 'Company / association name *',
    country: 'Country *',
    tool: 'AI tool or app name *',
    url: 'Public URL (optional)',
    email: 'Contact email *',
    justification: 'Justification (AI activity, anti-deepfake commitment…)',
    submit: 'Submit request',
    submitting: 'Sending…',
    ok: 'Request sent — an admin will review it.',
    ko: 'Error',
    duplicate: 'A request is already pending.',
    myRequests: 'My requests',
    statusPending: 'Pending admin approval',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    certNumber: 'Certificate number',
    downloadCert: 'Download PDF certificate',
    downloadLabel: 'Download SVG label',
    copyEndpoint: 'Copy check API URL',
    copied: 'Copied',
    hint: 'After approval, your AP-IA key (scope "Green AP-IA") can call /api/green-ia/external-check',
    proOnly: 'Verified PRO accounts only',
    why: 'Why Green IA? Deepfakes are exploding: stolen faces and voices fuel scams, harassment and disinformation. Green IA was created by Destine to give people back control: anyone can register their face and voice (stored only as irreversible SHA-256 hashes). Every Green IA-certified AI tool queries this registry BEFORE each generation and automatically blocks any deepfake of a protected person.',
    sdkTitle: 'Green AP-IA SDK — integrate the check in 3 lines',
    sdkNote: 'Call this endpoint before every image, video or voice generation. If `protected` is true, your tool must refuse the generation to stay certified.',
  },
  es: {
    title: 'Green AP-IA — Certificado ético anti-deepfake',
    intro: 'Únase a la iniciativa Green IA de Destine. Una vez aprobada su solicitud por un administrador, recibirá un certificado único + una etiqueta "Green IA" para mostrar en su herramienta o app. Su clave AP-IA podrá consultar nuestro registro Green IA antes de cualquier generación.',
    company: 'Nombre de la empresa / asociación *', country: 'País *',
    tool: 'Nombre de la herramienta o app de IA *', url: 'URL pública (opcional)',
    email: 'Correo de contacto *', justification: 'Justificación (actividad IA, compromiso anti-deepfake…)',
    submit: 'Enviar solicitud', submitting: 'Enviando…',
    ok: 'Solicitud enviada — un admin la revisará.', ko: 'Error', duplicate: 'Ya hay una solicitud pendiente.',
    myRequests: 'Mis solicitudes',
    statusPending: 'Pendiente de aprobación', statusApproved: 'Aprobado', statusRejected: 'Rechazado',
    certNumber: 'N.º de certificado', downloadCert: 'Descargar certificado PDF', downloadLabel: 'Descargar etiqueta SVG',
    copyEndpoint: 'Copiar URL de la API check', copied: 'Copiado',
    hint: 'Tras la aprobación, su clave AP-IA (scope "Green AP-IA") puede llamar a /api/green-ia/external-check',
    proOnly: 'Solo cuentas PRO verificadas',
    why: '¿Por qué Green IA? Los deepfakes se disparan: rostros y voces robados alimentan estafas y desinformación. Green IA devuelve el control a las personas: cada uno registra su rostro y su voz (solo hashes SHA-256 irreversibles). Toda herramienta certificada consulta este registro ANTES de cada generación y bloquea automáticamente cualquier deepfake de una persona protegida.',
    sdkTitle: 'SDK Green AP-IA — integra la verificación en 3 líneas',
    sdkNote: 'Llama a este endpoint antes de cada generación. Si `protected` es true, tu herramienta debe rechazar la generación.',
  },
  de: {
    title: 'Green AP-IA — Ethisches Anti-Deepfake-Zertifikat',
    intro: 'Treten Sie der Green-IA-Initiative von Destine bei. Nach Freigabe durch einen Administrator erhalten Sie ein einzigartiges Zertifikat + ein „Green IA"-Label zum Anzeigen in Ihrem Tool oder Ihrer App. Ihr AP-IA-Schlüssel kann dann unser Green-IA-Register vor jeder Erzeugung abfragen.',
    company: 'Firmen-/Vereinsname *', country: 'Land *', tool: 'Name des KI-Tools/der App *', url: 'Öffentliche URL (optional)',
    email: 'Kontakt-E-Mail *', justification: 'Begründung (KI-Aktivität, Anti-Deepfake-Engagement…)',
    submit: 'Anfrage senden', submitting: 'Sende…',
    ok: 'Anfrage gesendet — ein Admin prüft sie.', ko: 'Fehler', duplicate: 'Eine Anfrage ist bereits offen.',
    myRequests: 'Meine Anfragen',
    statusPending: 'Wartet auf Admin-Freigabe', statusApproved: 'Genehmigt', statusRejected: 'Abgelehnt',
    certNumber: 'Zertifikatsnummer', downloadCert: 'PDF-Zertifikat herunterladen', downloadLabel: 'SVG-Label herunterladen',
    copyEndpoint: 'Check-API-URL kopieren', copied: 'Kopiert',
    hint: 'Nach Genehmigung kann Ihr AP-IA-Schlüssel (Scope „Green AP-IA") /api/green-ia/external-check aufrufen',
    proOnly: 'Nur verifizierte PRO-Konten',
    why: 'Warum Green IA? Deepfakes explodieren: gestohlene Gesichter und Stimmen befeuern Betrug und Desinformation. Green IA gibt Menschen die Kontrolle zurück: Gesicht und Stimme werden nur als irreversible SHA-256-Hashes gespeichert. Jedes zertifizierte KI-Tool prüft dieses Register VOR jeder Generierung und blockiert Deepfakes geschützter Personen automatisch.',
    sdkTitle: 'Green AP-IA SDK — Prüfung in 3 Zeilen integrieren',
    sdkNote: 'Rufen Sie diesen Endpoint vor jeder Generierung auf. Ist `protected` true, muss Ihr Tool die Generierung ablehnen.',
  },
  it: {
    title: 'Green AP-IA — Certificato etico anti-deepfake',
    intro: "Unisciti all'iniziativa Green IA di Destine. Una volta approvata la tua richiesta da un amministratore, riceverai un certificato unico + un'etichetta \"Green IA\" da mostrare nel tuo strumento o app. La tua chiave AP-IA potrà quindi interrogare il nostro registro Green IA prima di qualsiasi generazione.",
    company: 'Nome azienda / associazione *', country: 'Paese *', tool: 'Nome dello strumento/app IA *', url: 'URL pubblica (opzionale)',
    email: 'Email di contatto *', justification: "Giustificazione (attività IA, impegno anti-deepfake…)",
    submit: 'Invia richiesta', submitting: 'Invio…',
    ok: 'Richiesta inviata — un admin la valuterà.', ko: 'Errore', duplicate: 'Richiesta già in attesa.',
    myRequests: 'Le mie richieste',
    statusPending: 'In attesa di approvazione', statusApproved: 'Approvato', statusRejected: 'Rifiutato',
    certNumber: 'N. certificato', downloadCert: 'Scarica certificato PDF', downloadLabel: 'Scarica etichetta SVG',
    copyEndpoint: "Copia URL dell'API check", copied: 'Copiato',
    hint: 'Dopo l\u2019approvazione, la tua chiave AP-IA (scope "Green AP-IA") può chiamare /api/green-ia/external-check',
    proOnly: 'Solo account PRO verificati',
    why: 'Perché Green IA? I deepfake esplodono: volti e voci rubati alimentano truffe e disinformazione. Green IA restituisce il controllo alle persone: volto e voce sono salvati solo come hash SHA-256 irreversibili. Ogni strumento certificato interroga questo registro PRIMA di ogni generazione e blocca automaticamente i deepfake delle persone protette.',
    sdkTitle: 'SDK Green AP-IA — integra la verifica in 3 righe',
    sdkNote: 'Chiama questo endpoint prima di ogni generazione. Se `protected` è true, il tuo strumento deve rifiutare la generazione.',
  },
  pt: {
    title: 'Green AP-IA — Certificado ético anti-deepfake',
    intro: 'Junte-se à iniciativa Green IA da Destine. Após aprovação pelo administrador, receberá um certificado único + um rótulo "Green IA" para exibir na sua ferramenta ou app. A sua chave AP-IA poderá então consultar o nosso registo Green IA antes de qualquer geração.',
    company: 'Nome da empresa/associação *', country: 'País *', tool: 'Nome da ferramenta/app IA *', url: 'URL público (opcional)',
    email: 'Email de contacto *', justification: 'Justificação (atividade IA, compromisso anti-deepfake…)',
    submit: 'Enviar pedido', submitting: 'A enviar…',
    ok: 'Pedido enviado — um admin irá analisar.', ko: 'Erro', duplicate: 'Já existe um pedido pendente.',
    myRequests: 'Os meus pedidos',
    statusPending: 'A aguardar aprovação', statusApproved: 'Aprovado', statusRejected: 'Recusado',
    certNumber: 'N.º de certificado', downloadCert: 'Descarregar certificado PDF', downloadLabel: 'Descarregar rótulo SVG',
    copyEndpoint: 'Copiar URL da API check', copied: 'Copiado',
    hint: 'Após aprovação, a sua chave AP-IA (scope "Green AP-IA") pode chamar /api/green-ia/external-check',
    proOnly: 'Apenas contas PRO verificadas',
    why: 'Por que Green IA? Os deepfakes explodem: rostos e vozes roubados alimentam golpes e desinformação. O Green IA devolve o controle às pessoas: rosto e voz são armazenados apenas como hashes SHA-256 irreversíveis. Toda ferramenta certificada consulta este registro ANTES de cada geração e bloqueia automaticamente deepfakes de pessoas protegidas.',
    sdkTitle: 'SDK Green AP-IA — integre a verificação em 3 linhas',
    sdkNote: 'Chame este endpoint antes de cada geração. Se `protected` for true, sua ferramenta deve recusar a geração.',
  },
  nl: {
    title: 'Green AP-IA — Ethisch anti-deepfake-certificaat',
    intro: 'Sluit u aan bij het Green IA-initiatief van Destine. Na goedkeuring door een beheerder ontvangt u een uniek certificaat + een "Green IA"-label om in uw tool of app te tonen. Uw AP-IA-sleutel kan dan ons Green IA-register raadplegen vóór elke generatie.',
    company: 'Naam bedrijf/vereniging *', country: 'Land *', tool: 'Naam AI-tool/app *', url: 'Openbare URL (optioneel)',
    email: 'Contact-email *', justification: 'Rechtvaardiging (AI-activiteit, anti-deepfake-inzet…)',
    submit: 'Aanvraag versturen', submitting: 'Versturen…',
    ok: 'Aanvraag verzonden — een admin beoordeelt.', ko: 'Fout', duplicate: 'Aanvraag hangt al.',
    myRequests: 'Mijn aanvragen',
    statusPending: 'Wacht op goedkeuring', statusApproved: 'Goedgekeurd', statusRejected: 'Afgewezen',
    certNumber: 'Certificaatnummer', downloadCert: 'PDF-certificaat downloaden', downloadLabel: 'SVG-label downloaden',
    copyEndpoint: 'Check-API-URL kopiëren', copied: 'Gekopieerd',
    hint: 'Na goedkeuring kan uw AP-IA-sleutel (scope "Green AP-IA") /api/green-ia/external-check aanroepen',
    proOnly: 'Alleen geverifieerde PRO-accounts',
    why: 'Waarom Green IA? Deepfakes exploderen: gestolen gezichten en stemmen voeden oplichting en desinformatie. Green IA geeft mensen de controle terug: gezicht en stem worden alleen als onomkeerbare SHA-256-hashes opgeslagen. Elke gecertificeerde AI-tool raadpleegt dit register VÓÓR elke generatie en blokkeert deepfakes van beschermde personen automatisch.',
    sdkTitle: 'Green AP-IA SDK — integreer de check in 3 regels',
    sdkNote: 'Roep dit endpoint aan vóór elke generatie. Is `protected` true, dan moet je tool de generatie weigeren.',
  },
  ar: {
    title: 'Green AP-IA — شهادة أخلاقية ضد التزييف العميق',
    intro: 'انضم إلى مبادرة Green IA من Destine. بعد الموافقة من قِبل مسؤول، ستحصل على شهادة فريدة + شعار "Green IA" لعرضه في أداتك أو تطبيقك. عندئذٍ يمكن لمفتاح AP-IA الخاص بك الاستعلام من سجلّ Green IA قبل أي توليد.',
    company: 'اسم الشركة/الجمعية *', country: 'البلد *', tool: 'اسم أداة/تطبيق الذكاء الاصطناعي *', url: 'رابط عام (اختياري)',
    email: 'بريد التواصل *', justification: 'مبررات (نشاط IA، الالتزام بمكافحة deepfake…)',
    submit: 'إرسال الطلب', submitting: 'جارٍ الإرسال…',
    ok: 'تم الإرسال — سيقوم مسؤول بالمراجعة.', ko: 'خطأ', duplicate: 'يوجد طلب معلق.',
    myRequests: 'طلباتي',
    statusPending: 'بانتظار الموافقة', statusApproved: 'مقبول', statusRejected: 'مرفوض',
    certNumber: 'رقم الشهادة', downloadCert: 'تحميل الشهادة PDF', downloadLabel: 'تحميل الشعار SVG',
    copyEndpoint: 'نسخ رابط API التحقق', copied: 'تم النسخ',
    hint: 'بعد الموافقة، يمكن لمفتاح AP-IA (النطاق "Green AP-IA") استدعاء /api/green-ia/external-check',
    proOnly: 'حسابات PRO موثّقة فقط',
    why: 'لماذا Green IA؟ التزييف العميق ينتشر: وجوه وأصوات مسروقة تُستخدم في الاحتيال والتضليل. أنشأت Destine مبادرة Green IA لإعادة السيطرة للأشخاص: يسجّل كل شخص وجهه وصوته (تُخزَّن فقط كبصمات SHA-256 غير قابلة للعكس). كل أداة ذكاء اصطناعي معتمدة تستعلم من هذا السجل قبل كل توليد وتحظر تلقائيًا أي تزييف لشخص محمي.',
    sdkTitle: 'SDK Green AP-IA — أدمج التحقق في 3 أسطر',
    sdkNote: 'استدعِ هذه الواجهة قبل كل توليد. إذا كانت `protected` صحيحة، يجب على أداتك رفض التوليد.',
  },
  ru: {
    title: 'Green AP-IA — Этический сертификат защиты от deepfake',
    intro: 'Присоединяйтесь к инициативе Green IA от Destine. После одобрения администратором вы получите уникальный сертификат + метку "Green IA" для показа в вашем инструменте или приложении. Ключ AP-IA сможет запрашивать реестр Green IA перед любой генерацией.',
    company: 'Название компании/ассоциации *', country: 'Страна *', tool: 'Название ИИ-инструмента/приложения *', url: 'Публичный URL (необязательно)',
    email: 'Контактный email *', justification: 'Обоснование (деятельность ИИ, обязательство против deepfake…)',
    submit: 'Отправить заявку', submitting: 'Отправка…',
    ok: 'Заявка отправлена — админ рассмотрит.', ko: 'Ошибка', duplicate: 'Заявка уже на рассмотрении.',
    myRequests: 'Мои заявки',
    statusPending: 'Ждёт одобрения', statusApproved: 'Одобрено', statusRejected: 'Отклонено',
    certNumber: '№ сертификата', downloadCert: 'Скачать PDF-сертификат', downloadLabel: 'Скачать SVG-метку',
    copyEndpoint: 'Скопировать URL API check', copied: 'Скопировано',
    hint: 'После одобрения ключ AP-IA (scope "Green AP-IA") может вызывать /api/green-ia/external-check',
    proOnly: 'Только верифицированные PRO-аккаунты',
    why: 'Зачем Green IA? Дипфейки стремительно растут: украденные лица и голоса используются для мошенничества и дезинформации. Green IA возвращает людям контроль: лицо и голос хранятся только в виде необратимых SHA-256-хэшей. Каждый сертифицированный ИИ-инструмент проверяет этот реестр ПЕРЕД каждой генерацией и автоматически блокирует дипфейки защищённых людей.',
    sdkTitle: 'SDK Green AP-IA — проверка в 3 строки',
    sdkNote: 'Вызывайте этот endpoint перед каждой генерацией. Если `protected` = true, инструмент обязан отклонить генерацию.',
  },
  zh: {
    title: 'Green AP-IA — 反深度伪造伦理证书',
    intro: '加入 Destine 的 Green IA 倡议。经管理员批准后，您将获得唯一证书 + "Green IA" 标识，用于在您的工具或应用中展示。您的 AP-IA 密钥即可在生成前查询 Green IA 名录。',
    company: '公司/机构名称 *', country: '国家 *', tool: 'AI 工具/应用名称 *', url: '公共 URL（可选）',
    email: '联系邮箱 *', justification: '理由（AI 业务、反深度伪造承诺…）',
    submit: '提交申请', submitting: '发送中…',
    ok: '申请已提交 — 管理员将审核。', ko: '错误', duplicate: '已存在待审核申请。',
    myRequests: '我的申请',
    statusPending: '待审核', statusApproved: '已通过', statusRejected: '已拒绝',
    certNumber: '证书编号', downloadCert: '下载 PDF 证书', downloadLabel: '下载 SVG 标识',
    copyEndpoint: '复制核验 API URL', copied: '已复制',
    hint: '获批后，您的 AP-IA 密钥（范围 "Green AP-IA"）可调用 /api/green-ia/external-check',
    proOnly: '仅限已认证 PRO 帐号',
    why: '为什么需要 Green IA？深度伪造正在激增：被盗的面孔和声音被用于诈骗和虚假信息。Green IA 让人们重新掌控自己：面部和声音仅以不可逆的 SHA-256 哈希存储。每个通过认证的 AI 工具在每次生成之前都会查询此注册表，并自动阻止对受保护人员的深度伪造。',
    sdkTitle: 'Green AP-IA SDK — 3 行代码集成验证',
    sdkNote: '在每次生成图像、视频或语音之前调用此接口。如果 `protected` 为 true，您的工具必须拒绝生成。',
  },
  ja: {
    title: 'Green AP-IA — 反ディープフェイク倫理証明書',
    intro: 'Destine の Green IA イニシアチブに参加しましょう。管理者の承認後、固有の証明書 + 「Green IA」ラベルが発行され、ツール／アプリに表示できます。AP-IA キーは生成前に Green IA 名簿を照会できるようになります。',
    company: '企業／団体名 *', country: '国 *', tool: 'AIツール／アプリ名 *', url: '公開URL（任意）',
    email: '連絡先メール *', justification: '理由（AI事業、反ディープフェイクの誓約…）',
    submit: '申請を送信', submitting: '送信中…',
    ok: '申請を送信しました — 管理者が確認します。', ko: 'エラー', duplicate: '既に申請保留中です。',
    myRequests: '自分の申請',
    statusPending: '承認待ち', statusApproved: '承認', statusRejected: '却下',
    certNumber: '証明書番号', downloadCert: 'PDF 証明書をダウンロード', downloadLabel: 'SVG ラベルをダウンロード',
    copyEndpoint: 'チェックAPI URL をコピー', copied: 'コピーしました',
    hint: '承認後、AP-IA キー（スコープ「Green AP-IA」）は /api/green-ia/external-check を呼び出せます',
    proOnly: '認証済み PRO アカウントのみ',
    why: 'なぜ Green IA？ディープフェイクは急増しており、盗まれた顔や声が詐欺や偽情報に悪用されています。Green IA は人々に主導権を取り戻します：顔と声は不可逆な SHA-256 ハッシュのみで保存されます。認証済みの AI ツールは生成のたびにこのレジストリを照会し、保護された人物のディープフェイクを自動的にブロックします。',
    sdkTitle: 'Green AP-IA SDK — 3 行でチェックを統合',
    sdkNote: '生成のたびにこのエンドポイントを呼び出してください。`protected` が true の場合、生成を拒否する必要があります。',
  },
  hi: {
    title: 'Green AP-IA — नैतिक एंटी-डीपफेक प्रमाणपत्र',
    intro: 'Destine की Green IA पहल में शामिल हों। एडमिन की मंज़ूरी के बाद आपको एक अद्वितीय प्रमाणपत्र + "Green IA" लेबल मिलेगा जिसे आप अपने टूल या ऐप में दिखा सकते हैं। आपकी AP-IA कुंजी हर बार जनरेट करने से पहले Green IA रजिस्ट्री से पूछ सकेगी।',
    company: 'कंपनी/संस्था का नाम *', country: 'देश *', tool: 'AI टूल/ऐप का नाम *', url: 'सार्वजनिक URL (वैकल्पिक)',
    email: 'संपर्क ईमेल *', justification: 'औचित्य (AI गतिविधि, एंटी-डीपफेक प्रतिबद्धता…)',
    submit: 'अनुरोध भेजें', submitting: 'भेज रहे हैं…',
    ok: 'अनुरोध भेजा — एडमिन जाँच करेगा।', ko: 'त्रुटि', duplicate: 'अनुरोध पहले से लंबित है।',
    myRequests: 'मेरे अनुरोध',
    statusPending: 'मंज़ूरी बाकी', statusApproved: 'स्वीकृत', statusRejected: 'अस्वीकृत',
    certNumber: 'प्रमाणपत्र क्रमांक', downloadCert: 'PDF प्रमाणपत्र डाउनलोड', downloadLabel: 'SVG लेबल डाउनलोड',
    copyEndpoint: 'चेक API URL कॉपी करें', copied: 'कॉपी हो गया',
    hint: 'मंज़ूरी के बाद, AP-IA कुंजी (scope "Green AP-IA") /api/green-ia/external-check को कॉल कर सकती है',
    proOnly: 'केवल सत्यापित PRO खाते',
    why: 'Green IA क्यों? डीपफेक तेज़ी से बढ़ रहे हैं: चुराए गए चेहरे और आवाज़ें धोखाधड़ी और दुष्प्रचार में इस्तेमाल होती हैं। Green IA लोगों को नियंत्रण लौटाता है: चेहरा और आवाज़ केवल अपरिवर्तनीय SHA-256 हैश के रूप में संग्रहीत होते हैं। हर प्रमाणित AI टूल हर जनरेशन से पहले इस रजिस्ट्री से पूछताछ करता है और संरक्षित व्यक्ति के डीपफेक को स्वतः ब्लॉक करता है।',
    sdkTitle: 'Green AP-IA SDK — 3 पंक्तियों में सत्यापन जोड़ें',
    sdkNote: 'हर जनरेशन से पहले इस endpoint को कॉल करें। यदि `protected` true है, तो आपके टूल को जनरेशन अस्वीकार करनी होगी।',
  },
  sw: {
    title: 'Green AP-IA — Cheti cha kimaadili dhidi ya deepfake',
    intro: 'Jiunge na juhudi ya Green IA ya Destine. Baada ya idhini ya msimamizi, utapokea cheti cha kipekee + lebo ya "Green IA" ya kuonyesha katika chombo au programu yako. Ufunguo wako wa AP-IA utaweza kuuliza rejista ya Green IA kabla ya kila utengenezaji.',
    company: 'Jina la kampuni/chama *', country: 'Nchi *', tool: 'Jina la chombo/programu ya AI *', url: 'URL ya umma (hiari)',
    email: 'Barua pepe ya mawasiliano *', justification: 'Uthibitisho (shughuli za AI, ahadi dhidi ya deepfake…)',
    submit: 'Tuma ombi', submitting: 'Tunatuma…',
    ok: 'Ombi limetumwa — msimamizi atakagua.', ko: 'Hitilafu', duplicate: 'Ombi tayari linasubiri.',
    myRequests: 'Maombi yangu',
    statusPending: 'Inasubiri idhini', statusApproved: 'Imeidhinishwa', statusRejected: 'Imekataliwa',
    certNumber: 'Nambari ya cheti', downloadCert: 'Pakua cheti cha PDF', downloadLabel: 'Pakua lebo ya SVG',
    copyEndpoint: 'Nakili URL ya API check', copied: 'Imenakiliwa',
    hint: 'Baada ya idhini, ufunguo wako wa AP-IA (scope "Green AP-IA") unaweza kupiga /api/green-ia/external-check',
    proOnly: 'Akaunti za PRO zilizohakikiwa tu',
    why: 'Kwa nini Green IA? Deepfake zinaongezeka: nyuso na sauti zilizoibiwa hutumika kwa ulaghai na upotoshaji. Green IA inarudisha udhibiti kwa watu: uso na sauti huhifadhiwa tu kama hash za SHA-256 zisizoweza kurudishwa. Kila zana ya AI iliyoidhinishwa huuliza sajili hii KABLA ya kila uzalishaji na kuzuia kiotomatiki deepfake ya mtu aliyelindwa.',
    sdkTitle: 'SDK ya Green AP-IA — unganisha ukaguzi kwa mistari 3',
    sdkNote: 'Ita endpoint hii kabla ya kila uzalishaji. Ikiwa `protected` ni true, zana yako lazima ikatae uzalishaji.',
  },
};
const SDK_SNIPPET = `POST /api/green-ia/external-check
Host: destine.studio
X-APIA-Key: VOTRE_CLE_AP_IA

{ "face_hash": "<sha256>", "voice_hash": "<sha256>" }

→ { "protected": true,  "action": "BLOCK_GENERATION" }
→ { "protected": false, "action": "ALLOW" }`;

const T = (lang) => ({ ...DICT.fr, ...(DICT[(lang || 'fr').toLowerCase()] || {}) });

export default function GreenApiaProPanel() {
  const { language } = useLanguage();
  const L = T(language);
  const [form, setForm] = useState({ company_name: '', country: '', tool_or_app_name: '', tool_url: '', contact_email: '', justification: '' });
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/green-ia/ap-ia/mine');
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.company_name.trim() || !form.country.trim() || !form.tool_or_app_name.trim() || !form.contact_email.trim()) {
      toast.error(L.ko);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/api/green-ia/ap-ia/request', form);
      toast.success(L.ok);
      setForm({ company_name: '', country: '', tool_or_app_name: '', tool_url: '', contact_email: '', justification: '' });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || L.ko;
      if (e?.response?.status === 409) toast.error(L.duplicate); else toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const downloadCert = async (id, certNumber) => {
    try {
      const r = await axios.get(`/api/green-ia/ap-ia/${id}/certificate`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Green-APIA-${certNumber || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(L.ko); }
  };

  const downloadLabel = () => {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backend}/api/green-ia/ap-ia/label.svg`, '_blank');
  };

  const copyEndpoint = async () => {
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    try {
      await navigator.clipboard.writeText(`${backend}/api/green-ia/external-check`);
      toast.success(L.copied);
    } catch { /* noop */ }
  };

  return (
    <div className="p-4 rounded-3xl bg-gradient-to-br from-green-950/30 to-emerald-950/30 border border-green-500/30" data-testid="green-apia-pro-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-green-200">{L.title}</h3>
          <p className="text-[11px] text-green-100/70 leading-snug mt-1">{L.intro}</p>
        </div>
      </div>

      {/* Why Green IA — detailed explanation (v50.15) */}
      <div className="mb-3 p-3 rounded-2xl bg-black/30 border border-green-500/20" data-testid="green-apia-why-block">
        <p className="text-[11px] text-green-100/80 leading-snug">{L.why}</p>
      </div>

      {/* SDK integration snippet (v50.15) */}
      <div className="mb-4 p-3 rounded-2xl bg-black/40 border border-emerald-500/25" data-testid="green-apia-sdk-block">
        <p className="text-xs font-bold text-emerald-200 mb-1.5">{L.sdkTitle}</p>
        <pre className="text-[10px] text-emerald-100/90 overflow-x-auto whitespace-pre leading-relaxed bg-black/50 rounded-xl p-2.5">{SDK_SNIPPET}</pre>
        <p className="text-[10px] text-green-100/60 mt-1.5">{L.sdkNote}</p>
      </div>

      {/* Existing requests */}
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-green-300 mx-auto my-4" />
      ) : items.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="green-apia-my-requests">
          <div className="text-[11px] font-bold text-green-200/80">{L.myRequests}</div>
          {items.map(it => (
            <div key={it.id} className={`p-3 rounded-xl border ${
              it.status === 'approved' ? 'bg-green-500/10 border-green-500/40' :
              it.status === 'rejected' ? 'bg-red-500/10 border-red-500/40' :
              'bg-black/40 border-white/10'
            }`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {it.company_name} · {it.tool_or_app_name}
                  </div>
                  <div className="text-[10px] text-white/60">{it.country} · {it.contact_email}</div>
                </div>
                <div className="shrink-0">
                  {it.status === 'approved' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-300 bg-green-500/20 px-2 py-0.5 rounded-full">
                      <BadgeCheck className="w-3 h-3" /> {L.statusApproved}
                    </span>
                  ) : it.status === 'rejected' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-300 bg-red-500/20 px-2 py-0.5 rounded-full">
                      <XIcon className="w-3 h-3" /> {L.statusRejected}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-yellow-300 bg-yellow-500/20 px-2 py-0.5 rounded-full">
                      <Clock className="w-3 h-3" /> {L.statusPending}
                    </span>
                  )}
                </div>
              </div>
              {it.status === 'approved' && it.certificate_number && (
                <>
                  <div className="mt-2 p-2 rounded-lg bg-black/50 text-[10px] text-green-100 font-mono">
                    {L.certNumber}: <span className="text-green-300">{it.certificate_number}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-1.5">
                    <button onClick={() => downloadCert(it.id, it.certificate_number)} className="col-span-2 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-[10px] font-semibold flex items-center justify-center gap-1" data-testid={`gapia-dl-cert-${it.id}`}>
                      <Download className="w-3 h-3" /> {L.downloadCert}
                    </button>
                    <button onClick={downloadLabel} className="py-2 rounded-full bg-white/10 hover:bg-white/20 text-[10px] font-semibold flex items-center justify-center gap-1" data-testid={`gapia-dl-label-${it.id}`}>
                      <Download className="w-3 h-3" /> SVG
                    </button>
                  </div>
                  <button onClick={copyEndpoint} className="mt-1.5 w-full py-2 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-[10px] text-emerald-200 font-semibold" data-testid={`gapia-copy-endpoint-${it.id}`}>
                    {L.copyEndpoint}
                  </button>
                  <p className="mt-1 text-[9px] text-white/40 italic">{L.hint}</p>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New request form */}
      {items.every(i => i.status !== 'pending') && (
        <div className="space-y-2" data-testid="green-apia-form">
          <input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder={L.company} className="w-full bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-green-400" data-testid="gapia-company" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder={L.country} className="bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-green-400" data-testid="gapia-country" />
            <input value={form.contact_email} type="email" onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder={L.email} className="bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-green-400" data-testid="gapia-email" />
          </div>
          <input value={form.tool_or_app_name} onChange={e => setForm({ ...form, tool_or_app_name: e.target.value })} placeholder={L.tool} className="w-full bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-green-400" data-testid="gapia-tool" />
          <input value={form.tool_url} onChange={e => setForm({ ...form, tool_url: e.target.value })} placeholder={L.url} className="w-full bg-black/40 border border-green-500/30 rounded-full px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-green-400" data-testid="gapia-url" />
          <textarea value={form.justification} onChange={e => setForm({ ...form, justification: e.target.value })} placeholder={L.justification} rows={3} className="w-full bg-black/40 border border-green-500/30 rounded-2xl px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-green-400" data-testid="gapia-justification" />
          <button onClick={submit} disabled={submitting} className="w-full py-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:brightness-110 disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2" data-testid="gapia-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {submitting ? L.submitting : L.submit}
          </button>
          <p className="text-[10px] text-white/40 text-center italic">{L.proOnly}</p>
        </div>
      )}
    </div>
  );
}
